// src/pages/WeekSchedulePage.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    Grid,
    MenuItem,
    TextField,
    Typography,
    Tabs,
    Tab,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { tokens } from "../assets/theme";
import {
    useWeekTemplates,
    useWeekTemplate,
    useUpdateWeekTemplate,
    useMachines,
    useAssignTemplate,
} from "../hooks/useWeekSchedule";
import { useMachineCalendar, useOverrideDay } from "../hooks/useProductionCalendar";
import WeekTemplateEditor from "../components/WeekTemplateEditor";
import OverrideDayDialog from "../components/OverrideDayDialog";

/* ---------- Styled tabs (samma stil som ActivitiesIndex) ---------- */

const StyledTabs = styled((props) => (
    <Tabs
        {...props}
        TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    />
))({
    "& .MuiTabs-indicator": {
        display: "flex",
        justifyContent: "center",
        backgroundColor: "transparent",
    },
    "& .MuiTabs-indicatorSpan": {
        width: "100%",
        backgroundColor: "#fff",
    },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
    ({ theme }) => ({
        textTransform: "none",
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: theme.typography.pxToRem(20),
        marginRight: theme.spacing(0),
        minWidth: "150px",
        color: "rgba(255, 255, 255, 0.7)",
        "&.Mui-selected": {
            color: "#fff",
            backgroundColor: "#434957 !important",
        },
        "&.Mui-focusVisible": {
            backgroundColor: "rgba(100, 95, 228, 0.32)",
        },
        "&.MuiTab-root": {
            backgroundColor: "#43495799",
            border: "1px solid #0c101b",
        },
    })
);

/* ---------- Tab 1: Veckoscheman ---------- */

function WeekTemplatesTab({ companyKey }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const { data: templates = [] } = useWeekTemplates(companyKey);
    const { data: template } = useWeekTemplate(
        typeof selectedTemplateId === "number" ? selectedTemplateId : null
    );
    const updateTemplateMutation = useUpdateWeekTemplate();

    const [localTemplate, setLocalTemplate] = useState(null);

    useEffect(() => {
        if (template) {
            setLocalTemplate(JSON.parse(JSON.stringify(template)));
        } else {
            setLocalTemplate(null);
        }
    }, [template]);

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const handleSave = async () => {
        if (!localTemplate || !localTemplate.id) return;

        await updateTemplateMutation.mutateAsync({
            id: localTemplate.id,
            template: localTemplate,
        });
    };

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Veckoscheman
            </Typography>

            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={4}>
                    <TextField
                        select
                        fullWidth
                        label="Veckoschema"
                        value={selectedTemplateId}
                        onChange={(e) =>
                            setSelectedTemplateId(
                                e.target.value === "" ? "" : Number(e.target.value)
                            )
                        }
                    >
                        <MenuItem value="">
                            <em>Välj schema</em>
                        </MenuItem>
                        {templates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                {localTemplate && (
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Namn på schema"
                            value={localTemplate.name || ""}
                            onChange={(e) =>
                                setLocalTemplate({
                                    ...localTemplate,
                                    name: e.target.value,
                                })
                            }
                        />
                    </Grid>
                )}
            </Grid>

            {localTemplate ? (
                <>
                    <WeekTemplateEditor
                        template={localTemplate}
                        onTemplateChange={setLocalTemplate}
                    />

                    <Box mt={3}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={
                                !localTemplate.id || updateTemplateMutation.isPending
                            }
                        >
                            Spara schema
                        </Button>
                    </Box>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Välj ett veckoschema för att redigera skift och namn.
                </Typography>
            )}
        </Box>
    );
}

/* ---------- Tab 2: Koppla schema till maskiner ---------- */

function AssignToMachinesTab({ companyKey }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [selectedMachineIds, setSelectedMachineIds] = useState([]);
    const [weekStart, setWeekStart] = useState(
        dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD")
    );
    const [validTo, setValidTo] = useState("");

    const { data: templates = [] } = useWeekTemplates(companyKey);
    const { data: machines = [] } = useMachines(companyKey);
    const assignTemplateMutation = useAssignTemplate();

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                  "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                  },
              }
            : {};

    const weekEndDefault = useMemo(
        () => dayjs(weekStart).add(6, "day").format("YYYY-MM-DD"),
        [weekStart]
    );

    const handleAssign = async () => {
        if (!selectedTemplateId || selectedMachineIds.length === 0) return;

        const vf = dayjs(weekStart).startOf("day").toISOString();
        const vt = validTo ? dayjs(validTo).endOf("day").toISOString() : null;

        for (const machineId of selectedMachineIds) {
            await assignTemplateMutation.mutateAsync({
                machineId: machineId,
                weekTemplateId: Number(selectedTemplateId),
                validFrom: vf,
                validTo: vt,
            });
        }
    };

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Koppla veckoschema till maskiner
            </Typography>

            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Vecka start (måndag)"
                        InputLabelProps={{ shrink: true }}
                        value={weekStart}
                        onChange={(e) => setWeekStart(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Giltig t.o.m."
                        InputLabelProps={{ shrink: true }}
                        value={validTo || ""}
                        placeholder={weekEndDefault}
                        onChange={(e) => setValidTo(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        fullWidth
                        label="Veckoschema"
                        value={selectedTemplateId}
                        onChange={(e) =>
                            setSelectedTemplateId(
                                e.target.value === "" ? "" : Number(e.target.value)
                            )
                        }
                    >
                        <MenuItem value="">
                            <em>Välj schema</em>
                        </MenuItem>
                        {templates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        fullWidth
                        label="Maskiner"
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((id) => {
                                        const m = machines.find((x) => x.id === id);
                                        return (
                                            <Chip
                                                key={id}
                                                label={m ? m.machineName : id}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            ),
                        }}
                        value={selectedMachineIds}
                        onChange={(e) =>
                            setSelectedMachineIds(
                                typeof e.target.value === "string"
                                    ? e.target.value.split(",").map(Number)
                                    : e.target.value
                            )
                        }
                    >
                        {machines.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                {m.machineName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>

            <Box mt={2}>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={
                        !selectedTemplateId ||
                        selectedMachineIds.length === 0 ||
                        assignTemplateMutation.isPending
                    }
                >
                    Koppla schema till maskiner
                </Button>
            </Box>
        </Box>
    );
}

/* ---------- Tab 3: Planerad kalender ---------- */
function PlannedCalendarTab({ companyKey }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedMachineId, setSelectedMachineId] = useState("");
    const [weekStart, setWeekStart] = useState(
        dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD")
    );

    const weekEnd = useMemo(
        () => dayjs(weekStart).add(6, "day").format("YYYY-MM-DD"),
        [weekStart]
    );

    const { data: machines = [] } = useMachines(companyKey);

    const { data: calendar = [], isFetching } = useMachineCalendar(
        selectedMachineId ? Number(selectedMachineId) : null,
        weekStart,
        weekEnd
    );

    const overrideMutation = useOverrideDay();

    const [overrideOpen, setOverrideOpen] = useState(false);
    const [selectedDayPlan, setSelectedDayPlan] = useState(null);

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                  "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                  },
              }
            : {};

    const handleOpenOverride = (dayPlan) => {
        setSelectedDayPlan(dayPlan);
        setOverrideOpen(true);
    };

    const handleCloseOverride = () => {
        setOverrideOpen(false);
        setSelectedDayPlan(null);
    };

    const refetchContext = {
        machineId: selectedMachineId ? Number(selectedMachineId) : null,
        fromDate: weekStart,
        toDate: weekEnd,
    };

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Planerad kalender
            </Typography>

            {/* Filter-rad: maskin + vecka */}
            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        fullWidth
                        label="Maskin"
                        value={selectedMachineId}
                        onChange={(e) => setSelectedMachineId(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>Välj maskin</em>
                        </MenuItem>
                        {machines.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                {m.machineName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Vecka start (måndag)"
                        InputLabelProps={{ shrink: true }}
                        value={weekStart}
                        onChange={(e) => setWeekStart(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        disabled
                        label="Vecka slut (söndag)"
                        value={weekEnd}
                    />
                </Grid>
            </Grid>

            {/* Innehåll: kalendern för vald maskin/vecka */}
            {selectedMachineId === "" ? (
                <Typography variant="body2" color="text.secondary">
                    Välj en maskin och vecka för att se planerad kalender.
                </Typography>
            ) : isFetching ? (
                <Typography variant="body2" color="text.secondary">
                    Hämtar kalender...
                </Typography>
            ) : calendar.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Ingen planerad kalender hittades för vald period.
                </Typography>
            ) : (
                <Box mt={2}>
                    <Grid container spacing={2}>
                        {calendar.map((day) => {
                            const date = dayjs(day.date);
                            const label = date.format("ddd DD/MM");
                            return (
                                <Grid item xs={12} md={6} lg={4} key={day.date}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            backgroundColor: day.isOverridden
                                                ? "rgba(255, 193, 7, 0.2)"
                                                : "rgba(0,0,0,0.15)",
                                            border: day.isOverridden
                                                ? "1px solid #ffc107"
                                                : "1px solid rgba(255,255,255,0.06)",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => handleOpenOverride(day)}
                                    >
                                        <Typography variant="subtitle1">
                                            {label} &mdash;{" "}
                                            {day.plannedProductionHours} h{" "}
                                            {day.isOverridden && "(överstyrd)"}
                                        </Typography>
                                        {day.shifts && day.shifts.length > 0 ? (
                                            day.shifts.map((s, idx) => (
                                                <Typography
                                                    key={idx}
                                                    variant="body2"
                                                >
                                                    {s.startTime.substring(
                                                        0,
                                                        5
                                                    )}{" "}
                                                    -{" "}
                                                    {s.endTime.substring(0, 5)}{" "}
                                                    ({s.label})
                                                </Typography>
                                            ))
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Inga skift definierade.
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                    >
                        Klicka på en dag för att överstyra planerade timmar och
                        skift.
                    </Typography>
                </Box>
            )}

            <OverrideDayDialog
                open={overrideOpen}
                onClose={handleCloseOverride}
                dayPlan={selectedDayPlan}
                machineId={selectedMachineId}
                overrideMutation={overrideMutation}
                refetchContext={refetchContext}
            />
        </Box>
    );
}

/* ---------- Wrapper med flikar ---------- */

function WeekSchedulePage() {
    const [activeTab, setActiveTab] = useState(0);
    const companyKey = 1; // byt till dynamiskt vid behov

    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Box
            p={2}
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
            <Box sx={{ width: "100%" }}>
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        m: "10px",
                        mb: 0,
                    }}
                >
                    <StyledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="Change between tabs"
                    >
                        <StyledTab label="Veckoscheman" />
                        <StyledTab label="Koppla till maskiner" />
                        <StyledTab label="Planerad kalender" />   
                    </StyledTabs>
                </Box>

                {activeTab === 0 && (
                    <WeekTemplatesTab companyKey={companyKey} />
                )}
                {activeTab === 1 && (
                    <AssignToMachinesTab companyKey={companyKey} />
                )}
                {activeTab === 2 && (
                    <PlannedCalendarTab companyKey={companyKey} />
                )}
            </Box>
        </Box>
    );
}

export default WeekSchedulePage;
